import { P2GBox, P2GText } from '@parcel2go/core-ui';
import {
  ConsignmentApiModel,
  Country,
  Dimensions,
  ExtraBase,
  InputRuleApiModel,
  MarketplaceOrderApiModel,
  PackageApiModel,
  QuoteApiModel,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import { GetItemWeight } from '../../../lib/helpers/SmartSendSummaryGenerator';
import { getLocalUnitsEntry } from '~/apps/smartsend/lib/helpers/LocalUnitsEntryHelper';
import { LocalUnitsEntry } from '~/apps/smartsend/lib/helpers/DimensionUnits';
import { ContentsSelection } from '../ContentsSelection';
import SidePanelSection, { SidePanelSections } from '../../../../../components/sidepanel-section';
import { ConsignmentDetails } from './ConsignmentDetails';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';
import { ProtectionExtraIds } from '../../ExtrasHelper';
import { ExtrasSelection } from '../ExtrasSelection';
import { getParcelCount, hasStoredParcels } from '~/apps/smartsend/lib/parcels/itemView';
import { ConsignmentContentsNotice } from './ConsignmentContentsNotice';
import { OrderLineSyncPlan, OrderLineSyncResult, planOrderLineSync } from '~/apps/smartsend/lib/parcels/syncOrderLines';
import { useMemo } from 'react';

interface props {
  item: SmartSendItemApiModel;
  tariffSystem: string;
  hideDimensionsIfNotRequired: boolean;
  getPackages: () => Promise<PackageApiModel[]>;
  onConsignmentUpdate: (c: ConsignmentApiModel) => void;
  onDimensionsUpdate: (c: Dimensions, localUnitsEntry?: LocalUnitsEntry) => void;
  onClose: () => void;
  getCountries: () => Promise<Country[]>;
  getValidation: () => Promise<{ ProductValidation: InputRuleApiModel[] }>;
  onContentUpdate?: (value: number, items: MarketplaceOrderApiModel[]) => void;
  currentSelection: ExtraBase[];
  getQuote: () => Promise<QuoteApiModel>;
  onExtrasUpdate: (extras: ExtraBase[]) => void;
  extrasWhiteList?: string[];
  extrasBlackList?: string[];
  canShowExtras?: boolean;
  /**
   * The lines as they stood when the parcels last agreed with them - the merge base for deciding
   * which line edits can be carried onto the parcels. Owned by ItemDetails because it has to advance
   * whenever the two are reconciled, which this panel cannot see.
   */
  baselineOrderLines?: MarketplaceOrderApiModel[];
  onApplyOrderLineSync?: (plan: OrderLineSyncPlan) => OrderLineSyncResult;
  onRebuildParcels?: () => void;
}

export const ItemContentsPanel: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  // Computed here rather than in the notice so the notice stays presentational, and so the same plan
  // object drives both the message and the write - re-deriving it on click could disagree with what
  // the user was shown.
  const syncPlan = useMemo(
    () => planOrderLineSync(props.item.Consignment, props.baselineOrderLines ?? [], props.item.MarketplaceOrderLines),
    [props.item.Consignment, props.baselineOrderLines, props.item.MarketplaceOrderLines]
  );

  return (
    <SidePanelSections>
      <SidePanelSection title={t('SHIPMENT_DETAILS_SUBTITLE', { defaultValue: 'Shipment Details:' })}>
        <ConsignmentDetails
          item={props.item}
          onUpdate={props.onConsignmentUpdate}
          getPackages={props.getPackages}
          dimensionsRequired={props.item.State?.validation?.QuoteState?.Quote?.Service?.DimensionsRequired}
          onDimensionsUpdate={props.onDimensionsUpdate}
          dimensions={props.item.PackageDimensions}
          localUnitsEntry={getLocalUnitsEntry(props.item.Meta)}
          hideDimensionsIfNotRequired={props.hideDimensionsIfNotRequired}
          onClose={props.onClose}
          getCountries={props.getCountries}
          getValidation={props.getValidation}
          tariffSystem={props.tariffSystem}
        />
      </SidePanelSection>
      {/* hasStoredParcels, matching ContentsSelectionRow: an empty-Parcels consignment declares
            nothing and takes nothing over, so there is nothing to explain and nothing to clear. */}

      {props.canShowExtras && (
        <ExtrasSelection
          hideLoader={true}
          extrasWhiteList={props.extrasWhiteList}
          extrasBlackList={props.extrasBlackList}
          item={props.item}
          showNonFoundMessage={false}
          getQuote={props.getQuote}
          currentSelection={props.currentSelection}
          onUpdate={(o) => {
            const _extras = props.currentSelection.filter((x) => ProtectionExtraIds.includes(x.Id));
            o?.forEach((e) => {
              if (!_extras.find((x) => x.Id === e.Id)) {
                _extras.push(e);
              }
            });
            props.onExtrasUpdate(_extras);
          }}
          wrapper={(x) => <>{x}</>}
        />
      )}
      <SidePanelSection
        title={t('ECOMMERCE_PRODUCT_SUMMARY_SUBTITLE', { defaultValue: 'Imported eCommerce Product Summary:' })}
        subText={
          <P2GBox>
            <P2GText variant="body2" component="p">
              {t('ECOMMERCE_PRODUCT_DESCRIPTION', {
                defaultValue: 'These are the product details imported from the eCommerce platform.'
              })}
            </P2GText>
          </P2GBox>
        }
      >
        <P2GBox mt={2}>
          <ContentsSelection
            item={props.item}
            items={props.item.MarketplaceOrderLines}
            countries={props.getCountries}
            getValidation={props.getValidation}
            tariffSystem={props.tariffSystem}
            onUpdate={props.onContentUpdate}
            totalWeight={GetItemWeight(props.item)}
            currencyCode={props.item.CurrencyCode}
            openContents={
              hasStoredParcels(props.item) && (
                <ConsignmentContentsNotice
                  parcelCount={getParcelCount(props.item)}
                  plan={syncPlan}
                  onApply={() => props.onApplyOrderLineSync?.(syncPlan)}
                  onRebuild={props.onRebuildParcels}
                />
              )
            }
          />
        </P2GBox>
      </SidePanelSection>
    </SidePanelSections>
  );
};
